import { StyledCentredContentBlock } from '@/components/blocks/CentredContentBlock'
import styled from 'styled-components'
import { theme } from 'styles/theme'

const StyledSciencePagePredictStudiesBlock = styled(StyledCentredContentBlock)`
  &.medium {
    display: flex;
    flex-direction: column;
    padding-left: 24px;
    padding-right: 24px;

    h2 {
      font-size: ${(p) => p.theme.v2.typography.display.xxs.fontSize};
      line-height: ${(p) => p.theme.v2.typography.display.xxs.lineHeight};
      font-family: ${(p) => p.theme.v2.font.nantes};
      color: ${(props) => props.theme.v2.onSurface.text};
      margin-bottom: 60px;
      text-align: center;

      @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
        font-size: ${(p) => p.theme.v2.typography.body.xl.fontSize};
        line-height: ${(p) => p.theme.v2.typography.body.xl.lineHeight};
        margin-bottom: 32px;
      }
    }
  }
`

const StyledStudiesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr;
  gap: 47px;
  margin-bottom: 46px;

  @media screen and (max-width: ${(p) => p.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`

const StyledPredictStudyBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: white;
  border-radius: 16px;
  padding: 24px;
  min-width: 270px;
  max-width: 400px;

  .predictIcon {
    margin-bottom: 36px;
    width: 185px;
  }

  h4 {
    font-size: ${(p) => p.theme.v2.typography.label.m.fontSize};
    line-height: ${(p) => p.theme.v2.typography.label.m.lineHeight};
    font-family: ${(p) => p.theme.v2.font.lotaGrotesqueLight};
    color: ${(props) => props.theme.v2.onSurface.text};
    text-align: left;
  }

  p {
    font-size: ${(p) => p.theme.v2.typography.body.xs.fontSize};
    line-height: ${(p) => p.theme.v2.typography.body.xs.lineHeight};
    font-family: ${(p) => p.theme.v2.font.lotaGrotesqueLight};
    color: ${(props) => props.theme.v2.onSurface.text};
    text-align: left;
  }

  .details {
    display: flex;
    align-items: center;

    img {
      margin-right: 8px;
    }
  }

  .partnersIcon {
    height: 24px;
    display: inline-block;

    &:not(:last-of-type) {
      margin-right: 14px;
    }
  }
`

type Icon = {
  path: string
  alt: string
}

type PredictStudy = {
  icon: Icon
  overview: string
  participant: string
  startDate: string
  completionDate: string
  partnersIcons: Icon[]
}

const STUDIES: PredictStudy[] = [
  {
    icon: {
      path: '/assets/img/science/predict1.svg',
      alt: 'Predict 1 Study',
    },
    overview:
      'We measured biological responses to specific foods in 1,106 people over 14 days, measuring blood glucose and fat levels, activity, inflammation, sleep and gut microbiome diversity. Our findings have been published in Nature Medicine in June 2020.',
    participant: '1,106',
    startDate: 'June 2018',
    completionDate: 'September 2019',
    partnersIcons: [
      {
        path: '/assets/img/science/Kings.png',
        alt: 'Kings College London',
      },
      {
        path: '/assets/img/science/Massachusetts.png',
        alt: 'Massachusetts General Hospital',
      },
    ],
  },
  {
    icon: {
      path: '/assets/img/science/predict2.svg',
      alt: 'Predict 2 Study',
    },
    overview:
      'An expanded home-based study that opened in June 2019. We recruited 1,200 volunteers across the US, measuring nutritional responses to food over a period of 11 days.',
    participant: '1,200',
    startDate: 'June 2019',
    completionDate: 'March 2020',
    partnersIcons: [
      {
        path: '/assets/img/science/Stanford.png',
        alt: 'Standford Medicine',
      },
      {
        path: '/assets/img/science/Massachusetts.png',
        alt: 'Massachusetts General Hospital',
      },
    ],
  },
  {
    icon: {
      path: '/assets/img/science/predict3.svg',
      alt: 'Predict 3 Study',
    },
    overview:
      'The PREDICT 3 study will build on previous research in over 2,000 individuals to further refine machine learning models that predict individual responses to foods, with the aim of advancing precision nutrition science and individualized dietary advice.',
    participant: '100,000',
    startDate: 'July 2020',
    completionDate: 'Ongoing',
    partnersIcons: [
      {
        path: '/assets/img/science/Kings.png',
        alt: 'Kings College London',
      },
      {
        path: '/assets/img/science/Massachusetts.png',
        alt: 'Massachusetts General Hospital',
      },
    ],
  },
]

const PredictStudyBlock: React.FC<PredictStudy> = ({
  icon,
  overview,
  participant,
  startDate,
  completionDate,
  partnersIcons,
}) => {
  return (
    <StyledPredictStudyBlock>
      <div>
        <img src={icon.path} alt={icon.alt} className="predictIcon" />

        <h4>STUDY OVERVIEW</h4>
        <p>{overview}</p>
      </div>

      <div>
        <p className="details">
          <img src="/assets/img/science/participant.svg" alt="Participant" />
          Participants: {participant}
        </p>
        <p className="details">
          <img src="/assets/img/science/started.svg" alt="Start date" />
          Started: {startDate}
        </p>
        <p className="details">
          <img src="/assets/img/science/completed.svg" alt="Completion date" />
          Completed: {completionDate}
        </p>

        <hr />
        <p>In partnership with researchers at:</p>
        <div>
          {partnersIcons.map((icon) => (
            <img src={icon.path} alt={icon.alt} key={icon.alt} className="partnersIcon" />
          ))}
        </div>
      </div>
    </StyledPredictStudyBlock>
  )
}

export const SciencePagePredictStudiesBlock = () => {
  return (
    <StyledSciencePagePredictStudiesBlock $bgColor={theme.v2.surface.cream500} className="medium">
      <h2>ZOE PREDICT studies</h2>

      <StyledStudiesContainer>
        {STUDIES.map((study, index) => (
          <PredictStudyBlock {...study} key={index} />
        ))}
      </StyledStudiesContainer>
    </StyledSciencePagePredictStudiesBlock>
  )
}
